/* Global top bar — 56px (h-14), midground surface, hairline bottom border.
   Mirrors andavo-dev's NavToolbar shape: logo left, global search center-right,
   user avatar far right. On small viewports a hamburger (left) toggles the
   mobile nav drawer (see AppShell).

   Auth-agnostic: the user + sign-out action are passed in as props. Each surface
   resolves the user from its own auth provider (labs = Entra/MSAL) and wires
   onSignOut to that provider's logout — the shell never imports an app's auth. */
import {
  ListIcon,
  MagnifyingGlassIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { CurrentUser } from '../types';
import { cn } from '../util/cn';
import { AndavoLogo } from './AndavoLogo';

export type TopBarProps = {
  user: CurrentUser;
  /** Called when the user picks "Sign out" from the account menu. */
  onSignOut?: () => void;
  /** Opens the mobile nav drawer (hamburger rendered only < lg). */
  onOpenNav?: () => void;
};

export function TopBar({ user, onSignOut, onOpenNav }: TopBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the account menu on outside-click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header
      id="toolbar"
      // `pl/pr` fold in the horizontal safe-area insets so the logo (left) and
      // account menu (right) stay clear of a notch / rounded corner in landscape
      // (viewport-fit=cover). Base 16px / sm: 24px is preserved via max().
      className="z-30 flex h-14 w-full shrink-0 items-center justify-between border-b border-border-subtle bg-surface-midground pr-[max(1rem,env(safe-area-inset-right))] pl-[max(1rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] sm:pl-[max(1.5rem,env(safe-area-inset-left))]"
    >
      <div className="flex items-center gap-2">
        {/* Mobile-only hamburger — toggles the nav drawer (hidden ≥ lg). */}
        {onOpenNav && (
          <button
            type="button"
            aria-label="Open navigation menu"
            data-testid="open-nav"
            onClick={onOpenNav}
            className="-ml-1 flex size-9 items-center justify-center rounded-md text-content-secondary hover:bg-surface-foreground lg:hidden"
          >
            <ListIcon className="size-icon-md" />
          </button>
        )}
        {/* The link carries its own name describing the DESTINATION, not just
            the graphic — a link named only "Andavo Labs" tells a screen-reader
            user what it shows, not where it goes. The lockup keeps its own
            role="img" name for the non-link surfaces (e.g. SignInPage). */}
        <Link
          aria-label="Andavo Labs home"
          className="flex items-center"
          to="/"
        >
          <AndavoLogo />
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-end gap-x-4">
        {/* Global search (⌘K). TODO(phase1): wire the command palette. */}
        <button
          type="button"
          className="hidden h-9 w-full max-w-sm items-center gap-2 rounded-md border border-border-subtle bg-surface-background px-3 text-content-secondary text-sm transition-colors hover:border-border-medium sm:flex"
        >
          <MagnifyingGlassIcon className="size-icon-sm" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="rounded border border-border-subtle bg-surface-foreground px-1.5 py-0.5 font-inter text-content-secondary text-xxs">
            ⌘K
          </kbd>
        </button>

        {/* Account menu — avatar chip toggles a dropdown with Sign out. */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-label={`Account: ${user.name}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="account-menu-trigger"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-button-primary font-inter font-semibold text-content-primary-inverted text-sm"
          >
            {user.initials}
          </button>

          {menuOpen && (
            <div
              role="menu"
              data-testid="account-menu"
              className={cn(
                'absolute right-0 top-11 z-40 w-56 overflow-hidden rounded-lg border border-border-card bg-surface-midground shadow-elevation-100',
              )}
            >
              <div className="border-border-subtle border-b px-4 py-3">
                <p className="truncate font-inter font-semibold text-content-primary text-sm">
                  {user.name}
                </p>
                <p className="truncate font-inter text-content-secondary text-xs">
                  {user.email}
                </p>
              </div>
              <button
                type="button"
                role="menuitem"
                data-testid="signout-button"
                onClick={() => {
                  setMenuOpen(false);
                  onSignOut?.();
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left font-inter text-content-primary text-sm transition-colors hover:bg-surface-foreground"
              >
                <SignOutIcon className="size-icon-sm text-content-secondary" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
